// `humanizeError` in `kit/useSendTransaction.ts` turns a custom program error
// into "The program rejected this (error 0x1771)", which is useless to a
// publisher. This maps the forum program's own errors to sentences a person can
// act on.
import type { AnchorError } from '@coral-xyz/anchor'
import { FORUM_IDL } from './program.js'

/**
 * Keyed by error NAME, never by number.
 *
 * Anchor numbers error variants by their position in the Rust enum, counting
 * from 6000 — so inserting a variant in the middle silently renumbers every
 * one below it. That already happened once: adding `NotSealed` next to
 * `AlreadySealed` shifted three codes, and a table keyed by number went on
 * confidently reporting "unknown hash algorithm" to someone who had too many
 * languages. Nothing failed, because a number lookup cannot fail — it just
 * answers wrong, which is worse than answering nothing.
 *
 * Names come from the same IDL the build regenerates whenever the enum
 * changes, so the two can no longer drift.
 */
const MESSAGES: Record<string, string> = {
  NotAuthorised: "Only this publisher's connected wallet may do this.",
  VideoSealed: 'This video was sealed and its content can never change.',
  AlreadySealed: 'This video is already sealed.',
  NotSealed:
    'A translation can only be added as a separate track once this video is sealed. While it can still be corrected, republish with the language included in the video itself.',
  LanguageSealed:
    'This language is part of the sealed base and cannot be added as a track — it would shadow sealed content.',
  TooManyLanguages: 'Too many sealed languages for one video.',
  UnknownHashAlgo: 'Unknown hash algorithm.',
  TooManyVersions: 'This container has reached the maximum number of versions.',
}

/**
 * Error code to sentence, resolved through the IDL's own code/name pairs.
 *
 * An error we have no friendlier wording for falls back to the program's own
 * `#[msg]` rather than vanishing: a new variant should read tersely, not
 * disappear into the generic "the program rejected this".
 */
const BY_CODE: Map<number, string> = new Map(
  FORUM_IDL.errors.map((e) => [e.code, MESSAGES[e.name] ?? e.msg]),
)

// The other direction of drift: a MESSAGES key matching no real IDL error
// (a typo, or a variant renamed on the Rust side) would sit here unused
// forever with nothing to notice.
//
// Loud in dev, where a rebuild puts it in front of whoever caused it, and
// merely noisy in production. Throwing here would run at import and take the
// whole admin UI down over a wrong error message — and `yarn build` never
// executes module top-level, so the typo would ship before anyone saw it.
const orphaned = Object.keys(MESSAGES).filter((n) => !FORUM_IDL.errors.some((e) => e.name === n))
if (orphaned.length > 0) {
  const complaint = `humanizeForumError: no IDL error matches ${orphaned.join(', ')} — renamed in lib.rs?`
  if (import.meta.env.DEV) throw new Error(complaint)
  console.error(complaint)
}

// Anchor custom program errors are numbered from 6000 (0x1770) up. A wallet
// that fails to parse the AnchorError surfaces the raw hex instead, so this
// must be checked too: 6000 -> 0x1770, 6001 -> 0x1771, and so on.
const CUSTOM_ERROR_HEX = /custom program error: (0x[0-9a-f]+)/i

/**
 * Maps a forum program error to a sentence a person can act on, or null if
 * this is not one of ours — the caller should fall back to `humanizeError`
 * for the generic case.
 */
export function humanizeForumError(err: unknown): string | null {
  // Walk the cause chain. useSendTransaction rethrows a generic sentence with
  // the original attached as `cause`, so the code we need is one level down —
  // checking only the top would quietly find nothing and every program error
  // would read "The program rejected this (error 0x1771)".
  for (let current = err, depth = 0; current != null && depth < 5; depth++) {
    const anchorCode = (current as { error?: AnchorError['error'] })?.error?.errorCode?.number
    if (typeof anchorCode === 'number') {
      const known = BY_CODE.get(anchorCode)
      if (known !== undefined) return known
    }

    const raw = current instanceof Error ? current.message : String(current)
    const match = raw.match(CUSTOM_ERROR_HEX)
    if (match) {
      const known = BY_CODE.get(parseInt(match[1], 16))
      if (known !== undefined) return known
    }

    // Solana surfaces the code inside simulation logs when the wallet did not
    // parse the error at all.
    const logs = (current as { logs?: unknown })?.logs
    if (Array.isArray(logs)) {
      const fromLogs = logs.join('\n').match(CUSTOM_ERROR_HEX)
      if (fromLogs) {
        const known = BY_CODE.get(parseInt(fromLogs[1], 16))
        if (known !== undefined) return known
      }
    }

    current = (current as { cause?: unknown })?.cause
  }

  return null
}
